// Shared date helpers for the trip detail view.
// Day 1 is the trip's `startDate` (YYYY-MM-DD) when it has one, otherwise the
// day it was created, which is what older trips always showed.

const WEEKDAY_OPTS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  month: "short",
  day: "numeric",
  year: "numeric",
};

const SHORT_OPTS: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };

/**
 * A formatter for the app's locale, falling back to English for a code Intl
 * does not know. These used to be `en-US` constants, which put "Friday, Sep
 * 18, 2026" at the top of the French, Arabic, Spanish, Italian, German and
 * Dutch trip pages.
 */
function formatter(lang: string, opts: Intl.DateTimeFormatOptions) {
  try {
    return new Intl.DateTimeFormat(lang, opts);
  } catch {
    return new Intl.DateTimeFormat("en-US", opts);
  }
}

/** What day 1 is anchored to: the chosen start date, else the creation time. */
export function tripStartOf(trip: { startDate?: string; createdAt: string }): string {
  return trip.startDate ?? trip.createdAt;
}

/**
 * Day 1 as a local Date. A bare YYYY-MM-DD is read as that calendar day where
 * the guest is: `new Date("2026-10-23")` would be UTC midnight, which is the
 * 22nd anywhere west of Greenwich.
 */
export function tripStartDate(createdAt: string): Date {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(createdAt);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return new Date();
  return d;
}

export function dayDate(createdAt: string, dayIndex: number): Date {
  const start = tripStartDate(createdAt);
  const d = new Date(start);
  d.setDate(d.getDate() + dayIndex);
  return d;
}

/** A day's date as YYYY-MM-DD, the form the bucket books it under. */
export function dayIso(start: string, dayIndex: number): string {
  const d = dayDate(start, dayIndex);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function formatDayLabel(
  createdAt: string,
  dayIndex: number,
  lang = "en",
): string {
  return formatter(lang, WEEKDAY_OPTS).format(dayDate(createdAt, dayIndex));
}

/**
 * Locale-aware short day date, e.g. "Mon, 9 Jun" (en) / "lun. 9 juin" (fr) /
 * "الإثنين، ٩ يونيو" (ar). Falls back to en-US for unknown locale codes.
 */
export function formatDayDateShort(
  createdAt: string,
  dayIndex: number,
  lang: string,
): string {
  const date = dayDate(createdAt, dayIndex);
  return formatter(lang, {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);
}

/** "23 Oct" (en) / "23 oct." (fr): one day of the trip, short. */
export function formatShortDay(start: string, dayIndex: number, lang: string): string {
  return formatter(`${lang}-u-nu-latn`, SHORT_OPTS).format(dayDate(start, dayIndex));
}

export function formatDateRange(
  createdAt: string,
  dayCount: number,
  lang = "en",
): string {
  if (dayCount <= 0) return "";
  const fmt = formatter(lang, SHORT_OPTS);
  const start = dayDate(createdAt, 0);
  const end = dayDate(createdAt, Math.max(0, dayCount - 1));
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

/** Deterministic time slot for the Nth stop within a day. */
export function stopTimeLabel(stopIndex: number): string {
  // 10:00 – 13:00, 13:30 – 16:30, 17:00 – 20:00, then loop.
  const slots = ["10:00 – 13:00", "13:30 – 16:30", "17:00 – 20:00"];
  return slots[stopIndex % slots.length] ?? "10:00 – 13:00";
}

/** HH:MM slots used as defaults when the user first edits a stop. */
export function defaultStopTime(stopIndex: number): {
  startTime: string;
  endTime: string;
} {
  const slots: readonly { startTime: string; endTime: string }[] = [
    { startTime: "10:00", endTime: "13:00" },
    { startTime: "13:30", endTime: "16:30" },
    { startTime: "17:00", endTime: "20:00" },
  ];
  return slots[stopIndex % slots.length] ?? slots[0];
}

/**
 * Picks the time label for a stop: user-edited (stored) times first,
 * deterministic slot fallback otherwise. Returns a full display
 * string like "10:00 – 13:00" or "From 10:00" if only the start
 * is set.
 */
export function resolveStopTimeLabel(
  stop: { startTime?: string; endTime?: string },
  stopIndex: number,
  /**
   * Translator for the one-sided cases. Optional because the .ics export has
   * no React context to read one from, and an exported calendar file is read
   * by a calendar app rather than shown on the page.
   */
  t?: (key: string, vars: Record<string, string>) => string,
): string {
  if (stop.startTime && stop.endTime) {
    return `${stop.startTime} – ${stop.endTime}`;
  }
  if (stop.startTime) {
    return t ? t("trip.time_from", { time: stop.startTime }) : `From ${stop.startTime}`;
  }
  if (stop.endTime) {
    return t ? t("trip.time_until", { time: stop.endTime }) : `Until ${stop.endTime}`;
  }
  return stopTimeLabel(stopIndex);
}

/**
 * "23 Oct" or "23 to 25 Oct": a trip's dates in the guest's language. Empty
 * when the trip has no start date. `range` words the two-day form, so the
 * caller's own "trip.when.range" string decides "to", "au", "-" and so on.
 */
export function formatTripDates(
  trip: { readonly startDate?: string; readonly days: { readonly length: number } },
  lang: string,
  range: (start: string, end: string) => string,
): string {
  if (!trip.startDate) return "";
  const first = formatShortDay(trip.startDate, 0, lang);
  if (trip.days.length <= 1) return first;
  return range(first, formatShortDay(trip.startDate, trip.days.length - 1, lang));
}
